import React from "react";
import { motion } from "framer-motion";
import type { TourStep } from "./tourSteps";
import { TOUR_STEPS } from "./tourSteps";

interface TourCalloutProps {
  targetRect: DOMRect;
  step: TourStep;
  stepIndex: number;
}

/** Render bold text from **markers** */
function renderMessage(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

const CALLOUT_WIDTH = 320;
const GAP = 16;

const TourCallout: React.FC<TourCalloutProps> = ({ targetRect, step, stepIndex }) => {
  const pos = step.calloutPosition;

  let top = 0;
  let left = 0;

  if (pos === "right") {
    top = targetRect.top + targetRect.height / 2;
    left = targetRect.left + targetRect.width + GAP;
  } else if (pos === "left") {
    top = targetRect.top + targetRect.height / 2;
    left = targetRect.left - CALLOUT_WIDTH - GAP;
  } else if (pos === "bottom") {
    top = targetRect.top + targetRect.height + GAP;
    left = targetRect.left + targetRect.width / 2 - CALLOUT_WIDTH / 2;
  } else {
    // top
    top = targetRect.top - GAP;
    left = targetRect.left + targetRect.width / 2 - CALLOUT_WIDTH / 2;
  }

  // Clamp to viewport
  left = Math.max(12, Math.min(left, window.innerWidth - CALLOUT_WIDTH - 12));
  top = Math.max(12, top);

  const transformOrigin =
    pos === "right" ? "left center" :
    pos === "left" ? "right center" :
    pos === "bottom" ? "top center" : "bottom center";

  const translateY = pos === "top" ? "translateY(-100%)" :
    pos === "right" || pos === "left" ? "translateY(-50%)" : undefined;

  // Arrow pointing toward the target
  const arrowSymbol =
    pos === "right" ? "←" :
    pos === "left" ? "→" :
    pos === "bottom" ? "↑" : "↓";

  return (
    <motion.div
      className="fixed z-[10003] pointer-events-none"
      style={{
        top,
        left,
        width: CALLOUT_WIDTH,
        transform: translateY,
        transformOrigin,
      }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-4 border border-white/10">
        {/* Arrow pointing at target */}
        <div className={`text-emerald-400 text-lg mb-1 ${pos === "right" ? "" : pos === "left" ? "text-right" : "text-center"}`}>
          {arrowSymbol}
        </div>

        <p className="text-sm leading-relaxed text-white/90">
          {renderMessage(step.message)}
        </p>
        {step.subMessage && (
          <p className="text-xs text-white/50 mt-2 leading-relaxed">
            {step.subMessage}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-white/30 font-medium">
            {stepIndex + 1} of {TOUR_STEPS.length}
          </span>
          <span className="text-[11px] text-white/40 font-medium">
            Click the highlighted area
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default TourCallout;
